import type { CSSProperties, ReactNode } from "react";
import { COLORS, SPACE, TYPE } from "../theme/index.js";

type EmptyStateKind = "no-data" | "no-match" | "error" | "loading";

const KIND_DEFAULTS: Record<EmptyStateKind, { title: string; description: string }> = {
  "no-data": {
    title: "Nothing here yet",
    description: "Data will appear once records are created.",
  },
  "no-match": { title: "No results", description: "Try adjusting your filters or search terms." },
  error: { title: "Something went wrong", description: "Refresh the page or try again shortly." },
  loading: { title: "Loading…", description: "" },
};

type EmptyStateProps = {
  kind?: EmptyStateKind;
  icon?: ReactNode;
  title?: string;
  description?: string;
  actions?: ReactNode;
  /** @deprecated use actions */
  cta?: ReactNode;
  style?: CSSProperties;
};

export function EmptyState({
  kind,
  icon,
  title,
  description,
  actions,
  cta,
  style,
}: EmptyStateProps) {
  const kindDefaults = kind ? KIND_DEFAULTS[kind] : undefined;
  const resolvedTitle = title ?? kindDefaults?.title ?? "";
  const resolvedDescription = description ?? kindDefaults?.description;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: SPACE[16],
        gap: SPACE[4],
        textAlign: "center",
        ...style,
      }}
    >
      {icon && <div style={{ color: COLORS.textDim, marginBottom: SPACE[2] }}>{icon}</div>}
      <h3
        style={{
          fontSize: TYPE.size.lg,
          fontWeight: TYPE.weight.semibold,
          color: COLORS.text,
          margin: 0,
        }}
      >
        {resolvedTitle}
      </h3>
      {resolvedDescription && (
        <p style={{ fontSize: TYPE.size.md, color: COLORS.textMuted, margin: 0, maxWidth: 400 }}>
          {resolvedDescription}
        </p>
      )}
      {(actions ?? cta) && (
        <div style={{ display: "flex", gap: SPACE[3], marginTop: SPACE[2] }}>{actions ?? cta}</div>
      )}
    </div>
  );
}
